import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import Link from 'next/link';
import { Mixins } from 'planoplan-ui-kit/core/mixins';
import { Colors } from 'planoplan-ui-kit/core/colors';
import { typographyText } from '@libs';
import { Points, textSize } from '@styles';
import { HELP_CENTER } from '@constants';

export const VideoCard = ({ id, cover, caption, slug, sectionUrl, category, bgcolor }) => {
  const [, setLoaded] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    const img = imgRef.current;

    // Сработает, даже если картинка в кеше и onLoad не вызовется
    if (img?.complete && img?.naturalWidth !== 0) {
      setLoaded(true);
    } else {
      // На случай, если она ещё не загружена
      const handleLoad = () => setLoaded(true);
      img?.addEventListener('load', handleLoad);

      return () => {
        img?.removeEventListener('load', handleLoad);
      };
    }
  }, [cover]);
  return (
    <Video key={id} bgColor={bgcolor}>
      <Link href={`/${HELP_CENTER.MAIN}/${sectionUrl}/${category}/${slug}`} passHref>
        <a aria-label={caption}>
          <div className="image-wrapper">
            <img
              ref={imgRef}
              src={cover?.length > 0 ? `${cover}` : `/images/help/placeholder.png`}
              alt={caption}
              key={`cover-${id}`}
              onLoad={() => setLoaded(true)}
              onError={() => console.warn('Image failed to load:', cover)}
            />
          </div>
          <Content>
            <Title>{typographyText(caption)}</Title>
          </Content>
        </a>
      </Link>
    </Video>
  );
};

const Video = styled.article`
  a {
    ${Mixins.resetLink()};
    display: flex;
    flex-direction: column;
    width: 340px;
    height: 100%;
    border: 1px solid #e1e1e1;

    @media ${Points.tablet_landscape} {
      width: 440px;
      min-height: 394px;
    }

    @media ${Points.laptop_wide} {
      width: 412px;
    }

    @media ${Points.fullhd} {
      width: 448px;
      min-height: 442px;
    }

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        border-color: ${Colors.main_blue};
        box-shadow: 0px 8px 24px -4px #29308614, 0px 6px 12px -6px #2930861f;
      }
    }

    &:active {
      border-color: ${Colors.main_blue};
      box-shadow: none;
    }
  }

    & div.image-wrapper {
      width: 100%;
      height: 190px;
      background-color: ${(p) => p.bgColor || Colors.main_beige};
      position: 'relative';
      overflow: 'hidden';
      position: 'absolute';
      top: 0;
      left: 0;
      objectFit: 'cover';
      opacity: loaded ? 1 : 0;
      transition: 'opacity 0.6s ease-in-out';

      @media ${Points.tablet_landscape} {
        height: 230px;
      }
  
      @media ${Points.fullhd}  {
        height: 250px;
      }
    }
  
    & img {
      width: 100%;
      height: 190px;
      overflow: revert !important;
      background-color: ${(p) => p.bgColor || Colors.main_beige};

      @media ${Points.tablet_landscape} {
        height: 230px;
      }
  
      @media ${Points.fullhd} {
        height: 250px;
      }
    }
  }
`;

const Content = styled.div`
  display: flex;
  justify-content: end;
  flex-direction: column;
  flex-grow: revert;
  padding: 40px 20px 20px;

  @media ${Points.tablet_landscape} {
    height: 164px;
  }

  @media ${Points.laptop_wide} {
    max-height: 164px;
  }

  @media ${Points.fullhd} {
    height: 192px;
  }
`;

const Title = styled.h2`
  justify-content: end;
  ${textSize('card_header')};
  color: ${Colors.main_dark};
  font-weight: 300;
  max-height: inherit;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;
